import * as THREE from 'three';
import { PET_BY_ID } from '../content/pets';
import type { ObservatorySession } from '../house/observatory';
import { OBSERVATORY_STATIONS } from '../house/observatory';
import { primitiveGeometry, toonMaterial } from './geometry';
import { meshOutline } from './outline';
import type { Renderer } from './renderer';
import { HOUSE_MODULE_BY_ID, type HouseModuleDef } from '../house/modules';

interface PetView { id: string; mesh: THREE.Mesh }

/** Primary-canvas presentation for the playable house map. */
export class ObservatoryView {
  readonly group = new THREE.Group();
  private readonly stationRings = new Map<string, THREE.Mesh>();
  private readonly pets: PetView[] = [];
  private readonly moduleGroup = new THREE.Group();
  private readonly previewGroup = new THREE.Group();
  private placementSignature = '';
  private previewModule = '';
  private time = 0;

  constructor(private readonly scene: THREE.Scene) {
    this.group.visible = false;
    this.group.add(this.moduleGroup, this.previewGroup);
    scene.add(this.group);
  }

  build(session: ObservatorySession): void {
    this.clear();
    this.time = 0;
    this.group.visible = true;

    const floor = toonMaterial(0x263945);
    const floorAlt = toonMaterial(0x1b2b34);
    const grass = toonMaterial(0x285b43);
    const wall = toonMaterial(0x35515f);
    const trim = toonMaterial(0x7895a0);
    const fence = toonMaterial(0x49666f);
    const metal = toonMaterial(0x1a2730, 0x62e5ff, 0.18);
    const wood = toonMaterial(0x765742);

    this.box(-6.25, -0.2, 0, 21.5, 0.35, 24, floor, false);
    this.box(10.75, -0.22, 0, 12.5, 0.3, 24, grass, false);
    // Tile insets make the interior read like the authored arena floors.
    for (let x = -15; x <= 3; x += 3) for (let z = -10.5; z <= 10.5; z += 3) this.box(x, 0.01, z, 2.82, 0.035, 2.82, (x + z) % 2 ? floor : floorAlt, false);

    // Roofless cutaway: tall rear/left walls and low front/right edges keep the
    // room legible from the normal isometric camera without hiding the player.
    this.box(-6.25, 2.2, -11.8, 21.8, 4.4, 0.45, wall);
    this.box(-16.8, 2.2, 0, 0.45, 4.4, 24, wall);
    this.box(-6.25, 0.45, 11.8, 21.8, 0.9, 0.35, trim);
    this.box(4.35, 0.45, -7.7, 0.35, 0.9, 8.2, trim);
    this.box(4.35, 0.45, 7.7, 0.35, 0.9, 8.2, trim);

    // Yard fence and path. The eastern wall remains open visually at the house
    // doorway but the full property is bounded at the map edge.
    this.box(10.7, 0.04, 1.4, 12, 0.08, 2.1, toonMaterial(0x63706f), false);
    this.box(16.8, 0.7, 0, 0.28, 1.4, 24, fence);
    this.box(10.7, 0.7, -11.8, 12.4, 1.4, 0.28, fence);
    this.box(10.7, 0.7, 11.8, 12.4, 1.4, 0.28, fence);

    // Chronolab workstation.
    this.cylinder(-9.5, 0.35, -5.5, 2.05, 0.7, metal);
    this.cylinder(-9.5, 1.25, -5.5, 0.72, 1.15, toonMaterial(0x21313b, 0x62e5ff, 0.55));
    this.ring(-9.5, 2.05, -5.5, 1.05, 0x62e5ff, Math.PI / 2);

    // Wardrobe and equipment rack.
    this.box(-13, 1.75, 5.8, 3.2, 3.5, 2.3, wood);
    this.box(-11.2, 1.15, 5.8, 0.25, 2.3, 2.8, trim);
    for (let i = 0; i < 3; i++) this.box(-14 + i * 0.82, 1.7, 4.55, 0.55, 2.4, 0.18, toonMaterial([0x62e5ff, 0xffb35c, 0xb48cff][i]));

    // Companion beds and supply chest.
    for (let i = 0; i < 3; i++) this.cylinder(-3.5 + i * 1.15, 0.18, 7.7 + (i % 2) * 0.75, 0.65, 0.24, toonMaterial([0x58d7ff, 0xffb34f, 0xd3a6ff][i]));
    this.box(-1.3, 0.7, 8.2, 1.4, 1.4, 1.4, wood);

    // Timeline gate, deliberately large enough to anchor the room but open in
    // the middle so later portal effects can be added without moving anything.
    this.box(-1.85, 2.2, -8.8, 0.45, 4.4, 0.8, metal);
    this.box(1.85, 2.2, -8.8, 0.45, 4.4, 0.8, metal);
    this.box(0, 4.2, -8.8, 4.1, 0.45, 0.8, metal);
    this.ring(0, 2.15, -8.72, 1.42, 0x86efff, 0);

    // Garden plots and glowing plants.
    this.box(10.8, 0.35, -4.5, 5.5, 0.7, 3.5, wood);
    for (let i = 0; i < 7; i++) {
      const x = 8.9 + (i % 4) * 1.25;
      const z = -5.2 + Math.floor(i / 4) * 1.5;
      this.cylinder(x, 0.92, z, 0.18, 1.1, toonMaterial(i % 2 ? 0x79df91 : 0x73d9c7, 0x55d879, 0.25));
    }

    // Telescope in the far yard.
    const telescope = this.cylinder(12.5, 1.15, 7, 0.22, 2.3, trim);
    telescope.rotation.z = -0.6;
    this.cylinder(12.5, 0.75, 7, 0.32, 1.5, metal);

    // Small furnishings and open expansion space through the center/right.
    this.box(-7, 0.55, 5.5, 3.2, 1.1, 1.5, wood);
    this.box(-7, 1.25, 6.1, 3.2, 1.4, 0.25, wood);
    this.box(-5.1, 0.7, 5.5, 0.9, 1.4, 0.9, wood);
    this.box(-8.9, 0.7, 5.5, 0.9, 1.4, 0.9, wood);

    this.syncModules(session, true);

    // Achievement cosmetics are permanent, profile-owned furnishings. Mastery
    // rewards hang as banners; feat and Paradox rewards fill the trophy shelf.
    const banners = session.cosmetics.filter((id) => id.startsWith('banner_'));
    banners.forEach((id, i) => {
      const color = this.cosmeticColor(id);
      const banner = this.box(-14.8 + i * 2.15, 2.35, -11.48, 1.55, 2.25, 0.12, toonMaterial(color, color, 0.12));
      banner.rotation.z = (i % 2 ? 1 : -1) * 0.025;
    });
    const trophies = session.cosmetics.filter((id) => !id.startsWith('banner_'));
    trophies.forEach((id, i) => {
      const x = -10.1 + (i % 7) * 1.15;
      const z = 10.65 - Math.floor(i / 7) * 0.75;
      const color = this.cosmeticColor(id);
      this.cylinder(x, 0.63, z, 0.28, 0.42, toonMaterial(0x4e6570));
      const trophy = new THREE.Mesh(primitiveGeometry(id.includes('paradox') ? 'octa' : 'sphere'), toonMaterial(color, color, 0.45));
      trophy.position.set(x, 1.18, z);
      trophy.scale.setScalar(id.includes('paradox') ? 0.48 : 0.38);
      trophy.castShadow = true;
      meshOutline(trophy, 0.045);
      this.group.add(trophy);
    });

    for (const station of OBSERVATORY_STATIONS) {
      const stationRing = this.ring(station.x, 0.08, station.z, 1.1, station.id === 'garden' ? 0x76d28d : 0x62e5ff, Math.PI / 2);
      stationRing.material.transparent = true;
      (stationRing.material as THREE.MeshBasicMaterial).opacity = 0.42;
      this.stationRings.set(station.id, stationRing);
      this.label(station.name, station.x, 2.8, station.z);
    }

    for (const state of session.pets) {
      const def = PET_BY_ID.get(state.id);
      if (!def) continue;
      const mesh = new THREE.Mesh(primitiveGeometry(def.geo), toonMaterial(def.color, def.color, 0.15));
      mesh.scale.setScalar(0.62);
      mesh.castShadow = true;
      meshOutline(mesh, 0.06);
      this.group.add(mesh);
      this.pets.push({ id: state.id, mesh });
    }
  }

  activate(renderer: Renderer): void {
    this.group.visible = true;
    renderer.worldHalf = 1_000;
    renderer.scene.background = new THREE.Color(0x07131c);
    renderer.scene.fog = new THREE.FogExp2(0x07131c, 0.018);
    renderer.ambient.color.set(0xc7efff);
    renderer.ambient.groundColor.set(0x183629);
    renderer.ambient.intensity = 1.35;
    renderer.sun.color.set(0xe8fbff);
    renderer.sun.intensity = 2.1;
  }

  update(session: ObservatorySession, dt: number): void {
    this.time += dt;
    this.syncModules(session);
    const near = session.nearStation()?.id;
    for (const [id, ring] of this.stationRings) {
      const selected = id === near;
      const scale = (selected ? 1.16 : 1) + Math.sin(this.time * 2.6) * (selected ? 0.06 : 0.025);
      ring.scale.setScalar(scale);
      (ring.material as THREE.MeshBasicMaterial).opacity = selected ? 0.85 : 0.36;
      ring.rotation.z += dt * (selected ? 0.9 : 0.28);
    }
    for (const pet of this.pets) {
      const state = session.pets.find((entry) => entry.id === pet.id);
      if (!state) continue;
      pet.mesh.position.set(state.x, 0.55 + Math.sin(this.time * 3 + state.x) * 0.09, state.z);
      pet.mesh.rotation.y = -state.facing + Math.PI / 2;
    }
  }

  private syncModules(session: ObservatorySession, force = false): void {
    const signature = session.placements.map((placement) => `${placement.id}:${placement.moduleId}:${placement.x}:${placement.z}:${placement.rotation}`).join('|');
    if (force || signature !== this.placementSignature) {
      this.clearGroup(this.moduleGroup);
      for (const placement of session.placements) {
        const def = HOUSE_MODULE_BY_ID.get(placement.moduleId);
        if (!def) continue;
        const object = this.moduleObject(def, false);
        object.position.set(placement.x, 0, placement.z);
        object.rotation.y = -placement.rotation;
        this.moduleGroup.add(object);
      }
      this.placementSignature = signature;
    }
    const preview = session.buildPreview;
    if (!preview) {
      this.previewGroup.visible = false;
      this.previewModule = '';
      this.clearGroup(this.previewGroup);
      return;
    }
    if (preview.moduleId !== this.previewModule) {
      this.clearGroup(this.previewGroup);
      const def = HOUSE_MODULE_BY_ID.get(preview.moduleId);
      if (def) this.previewGroup.add(this.moduleObject(def, true));
      this.previewModule = preview.moduleId;
    }
    this.previewGroup.visible = true;
    this.previewGroup.position.set(preview.x, 0.08 + Math.sin(this.time * 5) * 0.04, preview.z);
    this.previewGroup.rotation.y = -preview.rotation;
    this.previewGroup.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!(mesh.material instanceof THREE.MeshBasicMaterial)) return;
      mesh.material.color.set(preview.valid ? 0x76f3b1 : 0xff596b);
      mesh.material.opacity = 0.48;
    });
  }

  private moduleObject(def: HouseModuleDef, ghost: boolean): THREE.Group {
    const root = new THREE.Group();
    const material = (color: number) => ghost
      ? new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.48, depthWrite: false })
      : toonMaterial(color);
    const mesh = (geometry: THREE.BufferGeometry, color: number, x: number, y: number, z: number) => {
      const child = new THREE.Mesh(geometry, material(color));
      child.position.set(x, y, z);
      child.castShadow = !ghost;
      child.receiveShadow = true;
      if (!ghost) meshOutline(child, 0.025);
      root.add(child);
      return child;
    };
    const c = def.visual.color;
    const a = def.visual.accent;
    switch (def.visual.primitive) {
      case 'rug':
        mesh(new THREE.BoxGeometry(def.width, 0.08, def.depth), c, 0, 0.04, 0);
        mesh(new THREE.TorusGeometry(Math.min(def.width, def.depth) * 0.28, 0.055, 6, 24), a, 0, 0.1, 0).rotation.x = Math.PI / 2;
        break;
      case 'seat':
        mesh(new THREE.BoxGeometry(def.width, 0.55, def.depth), c, 0, 0.45, 0);
        mesh(new THREE.BoxGeometry(def.width, 1.15, 0.25), a, 0, 1, -def.depth * 0.38);
        break;
      case 'shelf':
        mesh(new THREE.BoxGeometry(def.width, 2.8, def.depth), c, 0, 1.4, 0);
        for (let i = -1; i <= 1; i++) mesh(new THREE.BoxGeometry(def.width * 0.82, 0.12, def.depth + 0.08), a, 0, 1.35 + i * 0.75, 0);
        break;
      case 'divider':
        mesh(new THREE.BoxGeometry(def.width, 2.8, 0.22), c, 0, 1.4, -def.depth / 2);
        mesh(new THREE.BoxGeometry(0.22, 2.8, def.depth), c, -def.width / 2, 1.4, 0);
        mesh(new THREE.BoxGeometry(def.width * 0.62, 0.8, def.depth * 0.45), a, 0.35, 0.55, 0.35);
        break;
      case 'table':
        mesh(new THREE.BoxGeometry(def.width, 0.25, def.depth), c, 0, 1, 0);
        for (const x of [-1, 1]) for (const z of [-1, 1]) mesh(new THREE.BoxGeometry(0.18, 1, 0.18), c, x * (def.width / 2 - 0.25), 0.5, z * (def.depth / 2 - 0.2));
        mesh(new THREE.OctahedronGeometry(0.28, 0), a, 0, 1.45, 0);
        break;
      case 'planter':
        mesh(new THREE.BoxGeometry(def.width, 0.55, def.depth), c, 0, 0.3, 0);
        for (let i = 0; i < 5; i++) mesh(new THREE.ConeGeometry(0.24, 1.1, 6), a, (i % 3 - 1) * 0.65, 0.95, (Math.floor(i / 3) - 0.3) * 0.75);
        break;
      case 'statue':
        mesh(new THREE.CylinderGeometry(0.65, 0.82, 0.45, 12), c, 0, 0.23, 0);
        mesh(new THREE.TorusGeometry(0.62, 0.1, 8, 28), a, 0, 1.35, 0).rotation.z = -0.55;
        mesh(new THREE.CylinderGeometry(0.12, 0.12, 1.4, 8), c, 0, 0.95, 0).rotation.z = -0.55;
        break;
      case 'lamp':
        mesh(new THREE.CylinderGeometry(0.13, 0.2, 1.5, 8), c, 0, 0.75, 0);
        mesh(new THREE.OctahedronGeometry(0.38, 0), a, 0, 1.65, 0);
        break;
    }
    root.userData.futureGltf = def.visual.gltf;
    return root;
  }

  hide(): void {
    this.group.visible = false;
  }

  private box(x: number, y: number, z: number, sx: number, sy: number, sz: number, material: THREE.Material, outline = true): THREE.Mesh {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), material);
    mesh.position.set(x, y, z);
    mesh.castShadow = outline;
    mesh.receiveShadow = true;
    if (outline) meshOutline(mesh, 0.025);
    this.group.add(mesh);
    return mesh;
  }

  private cylinder(x: number, y: number, z: number, radius: number, height: number, material: THREE.Material): THREE.Mesh {
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius * 1.06, height, 16), material);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    meshOutline(mesh, 0.035);
    this.group.add(mesh);
    return mesh;
  }

  private ring(x: number, y: number, z: number, radius: number, color: number, rotateX: number): THREE.Mesh<THREE.TorusGeometry, THREE.MeshBasicMaterial> {
    const mesh = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.055, 8, 48), new THREE.MeshBasicMaterial({ color, toneMapped: false }));
    mesh.position.set(x, y, z);
    mesh.rotation.x = rotateX;
    this.group.add(mesh);
    return mesh;
  }

  private label(text: string, x: number, y: number, z: number): void {
    const canvas = document.createElement('canvas');
    canvas.width = 384;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'rgba(5, 15, 23, .82)';
    ctx.fillRect(0, 4, canvas.width, 56);
    ctx.strokeStyle = '#62e5ff';
    ctx.strokeRect(1, 5, canvas.width - 2, 54);
    ctx.fillStyle = '#eafcff';
    ctx.font = '600 25px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text.toUpperCase(), canvas.width / 2, canvas.height / 2);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false }));
    sprite.position.set(x, y, z);
    sprite.scale.set(4.8, 0.8, 1);
    this.group.add(sprite);
  }

  private cosmeticColor(id: string): number {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = Math.imul(hash ^ id.charCodeAt(i), 16777619);
    const palette = [0x62e5ff, 0xffb35c, 0xb48cff, 0x6ee7a2, 0xff6f91, 0xf4df72];
    return palette[Math.abs(hash) % palette.length];
  }

  private clear(): void {
    for (const child of [...this.group.children]) {
      if (child === this.moduleGroup || child === this.previewGroup) continue;
      this.group.remove(child);
      child.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose();
        if (mesh.material) for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
          if (material instanceof THREE.SpriteMaterial) material.map?.dispose();
          material.dispose();
        }
      });
    }
    this.stationRings.clear();
    this.pets.length = 0;
    this.clearGroup(this.moduleGroup);
    this.clearGroup(this.previewGroup);
    this.group.add(this.moduleGroup, this.previewGroup);
    this.placementSignature = '';
    this.previewModule = '';
  }

  private clearGroup(group: THREE.Group): void {
    for (const child of [...group.children]) {
      group.remove(child);
      child.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose();
        if (mesh.material) for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) material.dispose();
      });
    }
  }
}
